/**
 * Safely parse JSON, handling common JSONB issues
 *
 * This function handles the PostgreSQL JSONB parsing issue where sometimes
 * there might be additional non-whitespace characters after the valid JSON.
 *
 * The error "Unexpected non-whitespace character after JSON at position X"
 * occurs because PostgreSQL JSONB might include additional metadata or characters.
 *
 * @param input The JSON string or object to parse
 * @param defaultValue The default value to return if parsing fails
 * @returns The parsed JSON object or the default value if parsing fails
 */
export function safeParseJson<T>(input: string | object | null | undefined, defaultValue: T): T {
  // If the input is null or undefined, return the default value
  if (input == null) return defaultValue;

  // If the input is already an object (not a string), just return it
  if (typeof input !== 'string') return input as unknown as T;

  // At this point, input is a string
  const jsonString = input;

  try {
    return JSON.parse(jsonString);
  } catch (e) {
    // Handle specific JSON parsing errors
    if (e instanceof SyntaxError) {
      try {
        // Case 1: Error mentions "Unexpected non-whitespace character after JSON"
        // or "position 4 (line 1 column 5)" which is a common JSONB error pattern
        if (
          e.message.includes('Unexpected non-whitespace character after JSON') ||
          e.message.includes('position 4') ||
          e.message.includes('Unexpected token')
        ) {
          // First, try the simplest approach - find valid JSON object/array with regex
          const objectMatch = jsonString.match(/^(\{.*\})/s);
          const arrayMatch = jsonString.match(/^(\[.*\])/s);

          if (objectMatch?.[1]) {
            return JSON.parse(objectMatch[1]);
          }

          if (arrayMatch?.[1]) {
            return JSON.parse(arrayMatch[1]);
          }

          // If regex fails, try more precise parsing by bracketing
          const firstOpenBrace = jsonString.indexOf('{');
          const firstOpenBracket = jsonString.indexOf('[');

          if (
            firstOpenBrace !== -1 &&
            (firstOpenBracket === -1 || firstOpenBrace < firstOpenBracket)
          ) {
            // Extract an object
            let bracketCount = 1;
            let inQuotes = false;
            let escapeNext = false;

            for (let i = firstOpenBrace + 1; i < jsonString.length; i++) {
              const char = jsonString[i];

              if (escapeNext) {
                escapeNext = false;
                continue;
              }

              if (char === '\\' && inQuotes) {
                escapeNext = true;
                continue;
              }

              if (char === '"' && !escapeNext) {
                inQuotes = !inQuotes;
              }

              if (!inQuotes) {
                if (char === '{') {
                  bracketCount++;
                } else if (char === '}') {
                  bracketCount--;
                  if (bracketCount === 0) {
                    return JSON.parse(jsonString.substring(firstOpenBrace, i + 1));
                  }
                }
              }
            }
          } else if (firstOpenBracket !== -1) {
            // Extract an array
            let bracketCount = 1;
            let inQuotes = false;
            let escapeNext = false;

            for (let i = firstOpenBracket + 1; i < jsonString.length; i++) {
              const char = jsonString[i];

              if (escapeNext) {
                escapeNext = false;
                continue;
              }

              if (char === '\\' && inQuotes) {
                escapeNext = true;
                continue;
              }

              if (char === '"' && !escapeNext) {
                inQuotes = !inQuotes;
              }

              if (!inQuotes) {
                if (char === '[') {
                  bracketCount++;
                } else if (char === ']') {
                  bracketCount--;
                  if (bracketCount === 0) {
                    return JSON.parse(jsonString.substring(firstOpenBracket, i + 1));
                  }
                }
              }
            }
          }

          // Last resort: Try stripping any non-visible or unexpected characters
          // This can handle the specific case mentioned in the query
          const cleaned = jsonString.replace(/[^\x20-\x7E]/g, '');
          return JSON.parse(cleaned);
        }
      } catch (innerError) {
        console.error('Failed to salvage JSON:', innerError);
        return defaultValue;
      }
    }

    console.error('Failed to parse JSON:', e);
    return defaultValue;
  }
}

export function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

// Barcode validation utilities
export * from './barcode';
